import { memo, ReactNode, useCallback } from 'react';
import { Link as RouterLink } from 'react-router';
import classes from 'classnames';
import styles from './Link.module.scss';
import { EventCollectorProps } from '@/hooks/context/EventCollector/types';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import LinkEvent from '@/hooks/context/EventCollector/events/LinkEvent';

type Props = {
  to?: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  external?: boolean;
  target?: '_blank' | '_self' | '_parent' | '_top';
  dataTest?: string;
  size?: 'm' | 's';
  onClick?: () => void;
  onMouseEnter?: () => void;
  loading?: boolean;
};

function Link({
  to = '',
  children,
  disabled = false,
  className,
  external = false,
  target = '_self',
  dataTest = 'link',
  size = 'm',
  onClick,
  eventCollector,
  onMouseEnter,
  loading,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleClick = useCallback(() => {
    onClick?.();

    if (eventCollector?.dontTrack) {
      return;
    }

    track(
      new LinkEvent({
        content: children?.toString() ?? to ?? '',
        description: to ? `link to ${to}` : 'link',
      }),
    );
  }, [eventCollector?.dontTrack, children, onClick, to, track]);

  if (disabled || loading)
    return (
      <span
        onMouseEnter={onMouseEnter}
        data-test={dataTest}
        data-testid={dataTest}
        className={classes(
          styles.link,
          styles[size],
          disabled && styles.disabled,
          loading && styles.loading,
          className,
        )}
      >
        {children}
      </span>
    );

  if (onClick) {
    return (
      <button
        onMouseEnter={onMouseEnter}
        type="button"
        onClick={handleClick}
        data-test={dataTest}
        data-testid={dataTest}
        className={classes(styles.link, styles[size], className)}
      >
        {children}
      </button>
    );
  }

  if (external)
    return (
      <a
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        data-test={dataTest}
        data-testid={dataTest}
        className={classes(styles.link, styles[size], className)}
        href={to}
        target={target}
        rel="noopener"
      >
        {children}
      </a>
    );

  return (
    <RouterLink
      onMouseEnter={onMouseEnter}
      data-test={dataTest}
      className={classes(styles.link, styles[size], className)}
      to={to}
      onClick={handleClick}
    >
      {children}
    </RouterLink>
  );
}

const LinkMemo = memo(Link);

export default LinkMemo;
